import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { ArrowRight } from "lucide-react";
import EditableText from "@/components/editor/EditableText";

const ATTRACTIONS = [
  {
    category: "On-Site",
    items: [
      {
        title: "The Tides",
        desc: "The biggest show on the property. Watch 21 feet of Bay of Fundy water disappear and return, twice a day. It never gets old. Bring a chair.",
      },
      {
        title: "Stargazing",
        desc: "Zero light pollution. On a clear night, the Milky Way arcs across the sky like you've only seen in photographs. Bring a blanket.",
      },
      {
        title: "Wildlife Watching",
        desc: "You might spot a seal surfacing offshore, or dolphins passing through the Bay. Whales move along the coast on migration. Fishing boats dot the horizon at dawn. No guarantees — that's part of why it's worth watching.",
      },
      {
        title: "Hiking the Shore",
        desc: "Trails from your campsite down through the woods to the water. Different at every tide.",
      },
      {
        title: "Sunsets over the Bay",
        desc: "The property faces directly west with an unobstructed view across the Bay of Fundy. No trees, no houses, no horizon line except water. The whole sky goes gold, then copper, then deep blue — most nights are a show. Bring a chair and stay until the last light goes.",
      },
      {
        title: "Dogs on the Land",
        desc: "Dogs are welcome — on-leash or off, you know your pup. The trails, the shoreline, the open land — all of it is fair game. Keep an eye on them around the tides and the wildlife, and pack out what they leave behind.",
      },
    ],
  },
  {
    category: "Beaches & Swimming",
    items: [
      {
        title: "Sandy Cove Beach",
        desc: "A protected pocket of actual sand tucked between the cliffs — a rarity on this coast. Easy on bare feet, warm in the sun, and a great spot to just sit and watch the tide work.",
      },
      {
        title: "Long Beach",
        desc: "True to its name — a long, open stretch of shoreline perfect for walking, beachcombing, and letting the kids (or dogs) run. Driftwood, sea glass, and big skies.",
      },
      {
        title: "Lake Midway Provincial Park",
        desc: "A short drive away. A warm freshwater lake with a sandy bottom — the perfect antidote to the cold Bay of Fundy when you want an actual swim. Picnic tables, easy access, quiet even in summer.",
      },
    ],
  },
  {
    category: "Off-Site",
    items: [
      {
        title: "Brier Island",
        desc: "Take the ferry across. World-class whale watching — humpbacks, fins, minkes, and the occasional right whale. Worth the trip every time.",
      },
      {
        title: "Balancing Rock",
        desc: "A column of basalt balanced on its edge for thousands of years. Short hike, big payoff. Bring a camera.",
      },
      {
        title: "Digby Scallops",
        desc: "Digby is the scallop capital of the world. Get them fresh off the boat at the wharf, or hit a local restaurant. You haven't had scallops until you've had Digby scallops.",
      },
      {
        title: "L.J. Robicheau & Sons",
        desc: "Fresh-off-the-boat seafood on the other side of Digby Neck. Scallops, haddock, lobster in season, whatever's running that day — bought straight from the people who pulled it out of the Bay that morning. Worth the drive, every time.",
      },
      {
        title: "Bear River",
        desc: "A tiny arts village tucked into the hills. Galleries, a vineyard, a general store that's been there forever. Charming without trying to be.",
      },
    ],
  },
];

export default function Attractions() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section id="attractions" data-testid="attractions-section" className="wc-section bg-white" ref={ref}>
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="mb-14"
        >
          <p className="font-accent italic text-wc-teal text-base mb-3">Things to Do</p>
          <EditableText contentKey="attractions_heading" as="h2"
            className="font-display text-wc-forest text-3xl sm:text-4xl lg:text-5xl font-semibold mb-4 max-w-2xl"
            defaultValue="A local's recommendations." />
          <EditableText contentKey="attractions_subtitle" as="p"
            className="font-body text-wc-slate text-base max-w-2xl"
            defaultValue="Not a tourism brochure. Just the stuff we'd tell a friend." />
        </motion.div>

        {ATTRACTIONS.map((section, si) => (
          <motion.div
            key={si}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: si * 0.15 }}
            className="mb-12"
          >
            <h3 className="font-display text-wc-brass text-lg font-semibold mb-6 pb-3 border-b border-wc-driftwood/30">
              {section.category}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {section.items.map((item, i) => (
                <div key={i} className="group" data-testid={`attraction-${si}-${i}`}>
                  <h4 className="font-display text-wc-forest text-base font-semibold mb-2 flex items-center gap-2">
                    {item.title}
                    <ArrowRight className="w-3 h-3 text-wc-teal opacity-0 group-hover:opacity-100 transition-opacity" />
                  </h4>
                  <p className="font-body text-wc-slate text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
